import React from "react";
import { useTheme } from "../contexts/ThemeContext";
import SEOHead from "./SEOHead";

const WhyShowAds = () => {
    const { colors } = useTheme();

    return (
        <>
            <SEOHead
                title="Why We Show Ads — Collegia App Transparency"
                description="Learn why Collegia shows ads. We're an independent student-led team building free academic tools for 50,000+ university students. Ads help us keep the app free."
                keywords="Collegia ads, why ads, Collegia app transparency, student app funding, free academic app, Chandigarh University app"
                url="https://collegia.app/ads"
            />
            <div className={`${colors.bg} min-h-screen py-8 px-4`}>
            <div className="max-w-4xl mx-auto">
                {/* Header Section */}
                <div className="text-center mb-12">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full mb-6">
                        <span className="text-2xl">💰</span>
                    </div>
                    <h1 className={`text-4xl md:text-6xl font-bold mb-4 ${colors.text} bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent`}>
                        Why do we show ads?
                    </h1>
                    <p className={`text-xl ${colors.textSecondary} font-light`}>
                        Transparency about our independent student-led initiative.
                    </p>
                </div>

                {/* Content Sections */}
                <div className="space-y-8">
                    {/* Section 1: Independent Development */}
                    <div className={`${colors.card} rounded-2xl p-8 border ${colors.border} backdrop-blur-sm`}>
                        <div className="flex items-start space-x-4">
                            <div className="flex-shrink-0 w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center">
                                <span className="text-blue-500">👨‍🎓</span>
                            </div>
                            <div>
                                <h2 className={`text-2xl font-semibold mb-4 ${colors.text}`}>
                                    Independent Student Development
                                </h2>
                                <p className={`${colors.textSecondary} text-lg leading-relaxed mb-4`}>
                                    This application is <span className={`${colors.text} font-semibold`}>entirely developed by students</span>, independent of any university involvement. 
                                    We created this app to provide a better user experience for accessing academic information through an improved interface.
                                </p>
                                <p className={`${colors.textSecondary} text-lg leading-relaxed`}>
                                    The university is <span className={`${colors.text} font-semibold`}>not involved in development, maintenance, or operation</span> of this application in any way.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Section 2: Technical Architecture */}
                    <div className={`${colors.card} rounded-2xl p-8 border ${colors.border} backdrop-blur-sm`}>
                        <div className="flex items-start space-x-4">
                            <div className="flex-shrink-0 w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center">
                                <span className="text-green-500">🔒</span>
                            </div>
                            <div>
                                <h2 className={`text-2xl font-semibold mb-4 ${colors.text}`}>
                                    Privacy-First Architecture
                                </h2>
                                <div className={`${colors.textSecondary} text-lg leading-relaxed space-y-3`}>
                                    <p>
                                        Our app functions as a <span className={`${colors.text} font-semibold`}>presentation layer only</span>. We do not store, cache, or permanently retain any university data:
                                    </p>
                                    <ul className="space-y-2 ml-6">
                                        <li className="flex items-start">
                                            <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                                            <span>All data processing happens <span className={`${colors.text} font-semibold`}>locally on your device</span></span>
                                        </li>
                                        <li className="flex items-start">
                                            <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                                            <span>You provide your own credentials directly to the university's system</span>
                                        </li>
                                        <li className="flex items-start">
                                            <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                                            <span>We merely present the data in a more user-friendly format</span>
                                        </li>
                                        <li className="flex items-start">
                                            <span className="w-2 h-2 bg-yellow-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                                            <span>No university data is transmitted to our servers</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Section 3: Infrastructure Costs */}
                    <div className={`${colors.card} rounded-2xl p-8 border ${colors.border} backdrop-blur-sm`}>
                        <div className="flex items-start space-x-4">
                            <div className="flex-shrink-0 w-8 h-8 bg-orange-500/20 rounded-full flex items-center justify-center">
                                <span className="text-orange-500">☁️</span>
                            </div>
                            <div>
                                <h2 className={`text-2xl font-semibold mb-4 ${colors.text}`}>
                                    Infrastructure & Development Costs
                                </h2>
                                <p className={`${colors.textSecondary} text-lg leading-relaxed mb-4`}>
                                    As independent students, we cover various costs to maintain this free service:
                                </p>
                                <div className="grid md:grid-cols-2 gap-4 mt-4">
                                    <div className="flex items-center space-x-3">
                                        <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                                        <span>App hosting and deployment</span>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                        <span>Security certificates and compliance</span>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                                        <span>Development tools and services</span>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                                        <span>Continuous improvements and updates</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Section 4: Legal Compliance */}
                    <div className={`${colors.card} rounded-2xl p-8 border ${colors.border} backdrop-blur-sm border-green-500/30 bg-green-500/5`}>
                        <div className="flex items-start space-x-4">
                            <div className="flex-shrink-0 w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center">
                                <span className="text-green-500">⚖️</span>
                            </div>
                            <div>
                                <h2 className={`text-2xl font-semibold mb-4 ${colors.text}`}>
                                    Our Legal Compliance Commitment
                                </h2>
                                <div className={`${colors.textSecondary} text-lg leading-relaxed space-y-3`}>
                                    <p>
                                        We operate in <span className={`${colors.text} font-semibold`}>full legal compliance</span> by:
                                    </p>
                                    <ul className="space-y-2 ml-6">
                                        <li className="flex items-start">
                                            <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                                            <span>Not storing or retaining any university data</span>
                                        </li>
                                        <li className="flex items-start">
                                            <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                                            <span>Processing all data locally on user devices</span>
                                        </li>
                                        <li className="flex items-start">
                                            <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                                            <span>Operating as an interface layer, not a data collector</span>
                                        </li>
                                        <li className="flex items-start">
                                            <span className="w-2 h-2 bg-yellow-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                                            <span>Maintaining transparent practices and open communication</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Section 5: Our Commitment */}
                    <div className={`${colors.card} rounded-2xl p-8 border ${colors.border} backdrop-blur-sm`}>
                        <div className="flex items-start space-x-4">
                            <div className="flex-shrink-0 w-8 h-8 bg-purple-500/20 rounded-full flex items-center justify-center">
                                <span className="text-purple-500">🤝</span>
                            </div>
                            <div>
                                <h2 className={`text-2xl font-semibold mb-4 ${colors.text}`}>
                                    Our Student Community Promise
                                </h2>
                                <div className={`${colors.textSecondary} text-lg leading-relaxed space-y-3`}>
                                    <p>
                                        We are committed to <span className={`${colors.text} font-semibold`}>serving fellow students</span> while maintaining the highest standards of privacy and security.
                                    </p>
                                    <p>
                                        The minimal revenue from ads covers only our operational costs - we are not profiting from this service. 
                                        Our goal is to keep this essential tool <span className={`${colors.text} font-semibold`}>free and accessible</span> for all students.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Call to Action */}
                    <div className="text-center mt-12 pt-8 border-t border-gray-700/20">
                        <h3 className={`text-2xl font-semibold mb-4 ${colors.text}`}>
                            Supporting Student Innovation 🚀
                        </h3>
                        <p className={`${colors.textSecondary} text-lg mb-6`}>
                            Your support helps us continue providing this free, secure service to the student community.
                        </p>
                        <div className="flex justify-center space-x-4">
                            <a 
                                href="/donate" 
                                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-green-500 to-blue-500 text-white font-semibold rounded-lg hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                            >
                                ☕ Support Us
                            </a>
                            <a 
                                href="/university_response" 
                                className={`inline-flex items-center px-6 py-3 ${colors.buttonSecondary} font-semibold rounded-lg hover:shadow-lg transition-all duration-300`}
                            >
                                📋 Our Response
                            </a>
                            <a 
                                href="/" 
                                className={`inline-flex items-center px-6 py-3 ${colors.buttonSecondary} font-semibold rounded-lg hover:shadow-lg transition-all duration-300`}
                            >
                                🏠 Back to Home
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </>
    );
};

export default WhyShowAds;
